/**
 * Rich Serac marketing footer (5-column grid).
 *
 * Used on the marketing surfaces: MarketingHome, Pricing.
 * Legal pages and login states still use the slimmer
 * components/common/MarketingFooter.tsx — that one is intentionally
 * minimal and stays as-is.
 */
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SeracWordmark } from '../serac';
import { useIsMobile, useIsTablet } from '../../hooks/useMediaQuery';
import FooterLanguageSwitch from './FooterLanguageSwitch';
import FooterThemeSwitch from './FooterThemeSwitch';

type FooterLink = { label: string; href?: string; to?: string };

const LABS_URL = 'https://seraclabs.dev';

export default function SeracMarketingFooter() {
  const { t } = useTranslation(['marketing', 'common']);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const gridCols = isMobile
    ? '1fr'
    : isTablet
    ? '1fr 1fr'
    : '1.3fr repeat(4, 1fr)';

  const cols: { heading: string; items: FooterLink[] }[] = [
    {
      heading: t('marketing:footer.columns.product'),
      items: [
        { label: t('marketing:footer.product.dashboard'), to: '/login' },
        { label: t('marketing:footer.product.cli'), href: 'https://github.com/serac-labs/serac/tree/main/packages/servicenow-mcp' },
        { label: t('marketing:footer.product.changelog'), href: 'https://github.com/serac-labs/serac/releases' },
        { label: t('marketing:footer.product.roadmap'), href: 'https://github.com/serac-labs/serac/issues' },
        { label: t('marketing:footer.product.status'), href: 'https://status.serac.build' },
      ],
    },
    {
      heading: t('marketing:footer.columns.resources'),
      items: [
        { label: t('marketing:footer.resources.useCases'), to: '/use-cases/ai-in-servicenow' },
        { label: t('marketing:footer.resources.blog'), to: '/blog' },
        { label: t('marketing:footer.resources.faq'), to: '/faq' },
        { label: t('marketing:footer.resources.docs'), href: 'https://docs.serac.build' },
        { label: t('marketing:footer.resources.guides'), href: 'https://docs.serac.build/guides' },
        { label: t('marketing:footer.resources.api'), href: 'https://docs.serac.build/api' },
      ],
    },
    {
      heading: t('marketing:footer.columns.community'),
      items: [
        { label: t('marketing:footer.community.github'), href: 'https://github.com/serac-labs/serac' },
        { label: t('marketing:footer.community.discord'), href: 'https://discord.gg/uGGrWj9KsT' },
        { label: t('marketing:footer.community.twitter'), href: 'https://x.com/seraclabs' },
      ],
    },
    {
      heading: t('marketing:footer.columns.company'),
      items: [
        { label: t('marketing:footer.company.about'), href: LABS_URL },
        { label: t('marketing:footer.company.brand'), href: `${LABS_URL}/brand` },
        { label: t('marketing:footer.company.security'), to: '/legal/privacy' },
        { label: t('marketing:footer.company.contact'), href: 'mailto:hello@serac.build' },
      ],
    },
  ];

  const linkStyle: CSSProperties = {
    fontSize: 13,
    color: 'var(--fg-muted)',
    cursor: 'pointer',
    textDecoration: 'none',
  };

  return (
    <footer
      style={{
        borderTop: '1px solid var(--border)',
        padding: isMobile ? '40px 16px 28px' : '56px 32px 40px',
        marginTop: 0,
        background: 'var(--bg)',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: isMobile ? 32 : 48,
          }}
        >
          <div>
            <SeracWordmark size={20} />
            <div
              style={{
                marginTop: 14,
                fontSize: 13,
                color: 'var(--fg-muted)',
                maxWidth: 240,
                lineHeight: 1.55,
              }}
            >
              {t('marketing:footer.tagline')}
            </div>
            <div
              style={{
                marginTop: 16,
                fontSize: 12,
                color: 'var(--fg-dim)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              <a
                href={LABS_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--ice-400)', textDecoration: 'none' }}
              >
                seraclabs.dev
              </a>{' '}
              · serac.build
            </div>
          </div>
          {cols.map(({ heading, items }) => (
            <div key={heading}>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--fg-dim)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.08em',
                  marginBottom: 14,
                }}
              >
                {heading.toUpperCase()}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {items.map((x, i) => {
                  if (x.to) {
                    return (
                      <Link key={i} to={x.to} style={linkStyle}>
                        {x.label}
                      </Link>
                    );
                  }
                  return (
                    <a
                      key={i}
                      href={x.href}
                      target={x.href!.startsWith('http') ? '_blank' : undefined}
                      rel={x.href!.startsWith('http') ? 'noopener noreferrer' : undefined}
                      style={linkStyle}
                    >
                      {x.label}
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: isMobile ? 32 : 56,
            paddingTop: isMobile ? 18 : 24,
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            color: 'var(--fg-dim)',
            flexWrap: 'wrap',
            gap: isMobile ? 12 : 16,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div>
            {t('marketing:footer.copyright', { year: new Date().getFullYear() })}
            {' · Built by '}
            <a
              href="https://groei.studio"
              target="_blank"
              rel="noopener"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Studio Groei
            </a>
          </div>
          <div
            style={{
              display: 'flex',
              gap: isMobile ? 14 : 20,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Link to="/legal/privacy" style={{ color: 'inherit' }}>
              {t('marketing:footer.privacy')}
            </Link>
            <Link to="/legal/terms" style={{ color: 'inherit' }}>
              {t('marketing:footer.terms')}
            </Link>
            <Link to="/legal/dpa" style={{ color: 'inherit' }}>
              {t('marketing:footer.dpa')}
            </Link>
            <Link to="/legal/sub-processors" style={{ color: 'inherit' }}>
              {t('marketing:footer.subprocessors')}
            </Link>
            <FooterThemeSwitch />
            <FooterLanguageSwitch />
          </div>
        </div>
      </div>
    </footer>
  );
}
